import { lazy, Suspense } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import LandingPage from "@/pages/LandingPage";
import PostHogPageview from "@/components/PostHogPageview";

const AppLayout = lazy(() => import("@/components/AppLayout"));
const PublicInAppLayout = lazy(() => import("@/components/PublicInAppLayout"));
const Auth = lazy(() => import("@/pages/Auth"));
const GuestReflection = lazy(() => import("@/pages/GuestReflection"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Journal = lazy(() => import("@/pages/Journal"));
const VoiceEntries = lazy(() => import("@/pages/VoiceEntries"));
const MoodTracker = lazy(() => import("@/pages/MoodTracker"));
const AIReflection = lazy(() => import("@/pages/AIReflection"));
const Insights = lazy(() => import("@/pages/Insights"));
const AffirmationsPage = lazy(() => import("@/pages/AffirmationsPage"));
const DiscoverPage = lazy(() => import("@/pages/DiscoverPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const DataDeletion = lazy(() => import("@/pages/DataDeletion"));
const Contact = lazy(() => import("@/pages/Contact"));
const JournalingTechniques = lazy(() => import("@/pages/JournalingTechniques"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const JournalPromptsForAnxiety = lazy(() => import("@/pages/JournalPromptsForAnxiety"));
const HowToStartJournaling = lazy(() => import("@/pages/HowToStartJournaling"));
const GratitudeJournalPrompts = lazy(() => import("@/pages/GratitudeJournalPrompts"));
const CBTThoughtRecord = lazy(() => import("@/pages/CBTThoughtRecord"));
const JournalingForStress = lazy(() => import("@/pages/JournalingForStress"));
const PromptGenerator = lazy(() => import("@/pages/PromptGenerator"));
const ShadowWorkJournalPrompts = lazy(() => import("@/pages/ShadowWorkJournalPrompts"));
const MorningJournalPrompts = lazy(() => import("@/pages/MorningJournalPrompts"));
const JournalingBeforeBed = lazy(() => import("@/pages/JournalingBeforeBed"));
const WhatIsAGuidedJournal = lazy(() => import("@/pages/WhatIsAGuidedJournal"));

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={localStorage.getItem("ip_pending_guest_reflection") ? "/reflect" : "/dashboard"} replace />;
  return <>{children}</>;
}

const LazyFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <PostHogPageview />
              <Suspense fallback={<LazyFallback />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />
                  <Route path="/try" element={<GuestReflection />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/journal" element={<Journal />} />
                    <Route path="/voice" element={<VoiceEntries />} />
                    <Route path="/mood" element={<MoodTracker />} />
                    <Route path="/reflect" element={<AIReflection />} />
                    <Route path="/insights" element={<Insights />} />
                    <Route path="/affirmations" element={<AffirmationsPage />} />
                    <Route path="/discover" element={<DiscoverPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Route>
                  <Route element={<PublicInAppLayout />}>
                    <Route path="/blog" element={<BlogIndex />} />
                    <Route path="/blog/journaling-techniques" element={<JournalingTechniques />} />
                    <Route path="/journal-prompts-for-anxiety" element={<JournalPromptsForAnxiety />} />
                    <Route path="/how-to-start-journaling" element={<HowToStartJournaling />} />
                    <Route path="/gratitude-journal-prompts" element={<GratitudeJournalPrompts />} />
                    <Route path="/cbt-thought-record" element={<CBTThoughtRecord />} />
                    <Route path="/journaling-for-stress" element={<JournalingForStress />} />
                    <Route path="/prompt-generator" element={<PromptGenerator />} />
                    <Route path="/shadow-work-journal-prompts" element={<ShadowWorkJournalPrompts />} />
                    <Route path="/morning-journal-prompts" element={<MorningJournalPrompts />} />
                    <Route path="/journaling-before-bed" element={<JournalingBeforeBed />} />
                    <Route path="/what-is-a-guided-journal" element={<WhatIsAGuidedJournal />} />
                  </Route>
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/data-deletion" element={<DataDeletion />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
